import type { ReactElement } from 'react'
import type { ContractUIComponentData } from 'tripPlanner/api/types'
import StagedActionConfirmationPrompt from './StagedActionConfirmationPrompt'
import type { UI_COMPONENT_TYPES } from './constants'

type ConfirmationPromptUIComponentData = Extract<
  ContractUIComponentData,
  { type: typeof UI_COMPONENT_TYPES.CONFIRMATION_PROMPT }
>

export type ConfirmationPromptPresentation = ConfirmationPromptUIComponentData['presentation']
type GenericConfirmationFallback = ConfirmationPromptPresentation['fallback']

export interface ConfirmationPromptRendererProps {
  pendingActionId: string
  selectedAction?: 'confirm' | 'cancel'
  isHistorical?: boolean
  presentation: ConfirmationPromptPresentation
  onSendMessage: (message: string) => void
}

type PromptRenderer = (
  props: Omit<ConfirmationPromptRendererProps, 'presentation'> & {
    presentation: ConfirmationPromptPresentation
    fallback: GenericConfirmationFallback
  },
) => ReactElement

type KnownConfirmationPromptRenderer = 'generic'

const knownConfirmationPromptRenderers = {
  generic: ({
    pendingActionId,
    selectedAction,
    isHistorical,
    fallback,
    onSendMessage,
  }) => (
    <StagedActionConfirmationPrompt
      pendingActionId={pendingActionId}
      selectedAction={selectedAction}
      isHistorical={isHistorical}
      title={fallback.title}
      body={fallback.body}
      confirmButtonLabel={fallback.confirmButtonLabel}
      cancelButtonLabel={fallback.cancelButtonLabel}
      onSendMessage={onSendMessage}
    />
  ),
} satisfies Record<KnownConfirmationPromptRenderer, PromptRenderer>

const confirmationPromptRenderers: Partial<Record<string, PromptRenderer>> =
  knownConfirmationPromptRenderers

function ConfirmationPromptRenderer(props: ConfirmationPromptRendererProps) {
  const fallback = props.presentation.fallback
  const requestedRenderer = confirmationPromptRenderers[props.presentation.component]
  const renderPrompt = requestedRenderer ?? knownConfirmationPromptRenderers.generic

  return renderPrompt({
    ...props,
    fallback,
  })
}

export default ConfirmationPromptRenderer
